import { useEffect, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';

import { fileUrl } from './api';
import { clock, eventLine, personLine, type Side } from './copy';
import { SHOTS_MAX, imagesOf, shotOf } from './shots';
import type { Shot, TicketMessage, TicketPerson } from './types';

export function Lightbox({ src, onClose }: { src: string | null; onClose: () => void }): ReactElement | null {
  useEffect(() => {
    if (!src) return;
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [src, onClose]);
  if (!src) return null;
  return (
    <button type="button" className="nx-tk__lb" onClick={onClose} aria-label="закрыть скрин">
      <img src={src} alt="скрин" />
    </button>
  );
}

type BubblesProps = {
  messages: TicketMessage[];
  side: Side;
  meId?: string | null;
  people?: Record<string, TicketPerson>;
  customer?: TicketPerson;
};

function authorOf(message: TicketMessage, props: BubblesProps): string {
  if (props.side === 'user') return message.author === 'user' ? 'вы' : 'команда FORQ';
  if (message.author === 'user') return personLine(props.customer, message.authorId);
  if (message.authorId === props.meId) return 'вы';
  const who = props.people?.[message.authorId];
  return who?.name ?? who?.username ?? 'FORQ';
}

export function Bubbles(props: BubblesProps): ReactElement {
  const { messages, side } = props;
  const [zoom, setZoom] = useState<string | null>(null);
  const end = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);
  return (
    <div className="nx-tk__msgs">
      {messages.map((message) => {
        if (message.event) {
          return (
            <p key={message.id} className={`nx-tk__event nx-tk__event--${message.event}`}>
              <span>{eventLine(message.event, message.author, side)}</span>
              <time>{clock(message.at)}</time>
            </p>
          );
        }
        const mine = message.author === side;
        return (
          <div key={message.id} className={`nx-tk__msg${mine ? ' nx-tk__msg--mine' : ''}${message.author === 'staff' ? ' nx-tk__msg--team' : ''}`}>
            <div className="nx-tk__meta">
              <b>{authorOf(message, props)}</b>
              <time>{clock(message.at)}</time>
            </div>
            {message.text && <p className="nx-tk__text">{message.text}</p>}
            {message.files.length > 0 && (
              <div className="nx-tk__pics">
                {message.files.map((file) => (
                  <button key={file.id} type="button" onClick={() => setZoom(fileUrl(file.id))} aria-label="открыть скрин">
                    <img src={fileUrl(file.id)} alt="скрин" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div ref={end} />
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}

type ComposerProps = {
  text: string;
  onText: (text: string) => void;
  shots: Shot[];
  onShots: (next: Shot[]) => void;
  onSend: () => void;
  busy: boolean;
  placeholder: string;
  sendLabel: string;
  canSend: boolean;
  rows?: number;
  below?: ReactNode;
  aside?: ReactNode;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
};

/** Поле ответа. Ctrl+V скрина работает, даже если курсор не в поле. */
export function Composer(props: ComposerProps): ReactElement {
  const { shots, onShots } = props;
  const root = useRef<HTMLDivElement | null>(null);
  const own = useRef<HTMLTextAreaElement | null>(null);
  const area = props.inputRef ?? own;
  const pick = useRef<HTMLInputElement | null>(null);
  const [drag, setDrag] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const latest = useRef(shots);
  latest.current = shots;

  const add = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    const room = SHOTS_MAX - latest.current.length;
    if (room <= 0) {
      setNote(`не больше ${SHOTS_MAX} скринов в одном сообщении`);
      return;
    }
    setNote(null);
    const made: Shot[] = [];
    for (const file of files.slice(0, room)) {
      try {
        made.push(await shotOf(file));
      } catch (failure) {
        setNote(failure instanceof Error ? failure.message : 'скрин не добавился');
      }
    }
    if (files.length > room) setNote(`не больше ${SHOTS_MAX} скринов в одном сообщении`);
    if (made.length > 0) onShots([...latest.current, ...made]);
  };

  useEffect(() => {
    const paste = (event: ClipboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const inside = target ? root.current?.contains(target) : false;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (!inside && typing) return;
      const files = imagesOf(event.clipboardData?.items);
      if (files.length === 0) return;
      if (!event.clipboardData?.getData('text/plain')) event.preventDefault();
      void add(files);
      area.current?.focus();
    };
    document.addEventListener('paste', paste);
    return () => document.removeEventListener('paste', paste);
  });

  useEffect(() => {
    const box = area.current;
    if (!box) return;
    box.style.height = 'auto';
    box.style.height = `${Math.min(box.scrollHeight, 320)}px`;
  }, [props.text, area]);

  return (
    <div
      ref={root}
      className={`nx-tk__compose${drag ? ' nx-tk__compose--drag' : ''}`}
      onDragOver={(event) => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return;
        event.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDrag(false);
        void add(imagesOf(event.dataTransfer.files));
      }}
    >
      <textarea
        ref={area}
        value={props.text}
        rows={props.rows ?? 4}
        placeholder={props.placeholder}
        maxLength={4000}
        onChange={(event) => props.onText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && props.canSend && !props.busy) {
            event.preventDefault();
            props.onSend();
          }
        }}
      />
      {shots.length > 0 && (
        <div className="nx-tk__thumbs">
          {shots.map((shot) => (
            <figure key={shot.key}>
              <img src={shot.data} alt="скрин" />
              <button type="button" aria-label="убрать скрин" onClick={() => onShots(shots.filter((item) => item.key !== shot.key))}>
                ×
              </button>
            </figure>
          ))}
        </div>
      )}
      {note && <p className="nx-tk__warn">{note}</p>}
      {props.below}
      <div className="nx-tk__bar">
        <button type="button" className="nx-tk__clip" onClick={() => pick.current?.click()} disabled={shots.length >= SHOTS_MAX}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" />
            <path d="m3 16 5-5 4 4 3-3 6 6" />
            <circle cx="16" cy="9" r="1.5" />
          </svg>
          <span>скрин</span>
          <kbd>Ctrl+V</kbd>
        </button>
        <input
          ref={pick}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(event) => {
            void add(imagesOf(event.target.files));
            event.target.value = '';
          }}
        />
        <div className="nx-tk__gap" />
        {props.aside}
        <button type="button" className="nx-tk__send" disabled={!props.canSend || props.busy} onClick={props.onSend}>
          {props.busy ? 'отправляем…' : props.sendLabel}
        </button>
      </div>
      {drag && <div className="nx-tk__drop">отпустите — скрин прикрепится</div>}
    </div>
  );
}
